import BaseService from './baseService'
import { packageAuthService } from '@/api'
import { userService } from './userService'
import { useUserStore } from '@/store/user'
import { useAppStore } from '@/store/app'
import { VueCookies } from 'vue-cookies'
import { User } from '@/types/user'
import {
  SendLoginOtpResponse,
  ValidateOtpRequest,
  ValidateOtpResponse
} from '@/types/authServiceTypes'
import NotifyError from '@/utils/NotifyError'

export class AuthService extends BaseService {
  constructor() {
    super()
  }

  /**
   * Generates PKCE parameters, saves them, and redirects browser to the IdP authorization URL.
   */
  async initiateAuthorize(
    redirectUrl: string = '/',
    prompt?: string
  ): Promise<void> {
    sessionStorage.setItem('dx_internal_redirect', redirectUrl)

    const resource = (import.meta.env.VITE_RESOURCE as string) || ''
    const callbackUri = window.location.origin + '/callback'

    const authUrl = await packageAuthService.createAuthorizationUrl(
      callbackUri,
      prompt,
      resource
    )
    window.location.href = authUrl
  }

  /**
   * Checks if valid PKCE verifier and matching state context exist in storage.
   */
  hasValidContext(state: string | null): boolean {
    if (!state) return false
    const verifier = localStorage.getItem('dx_auth_verifier')
    const savedState = localStorage.getItem('dx_auth_state')
    return Boolean(verifier && savedState && savedState === state)
  }

  /**
   * Validates the callback state, exchanges the code for a token, and returns the original URL.
   */
  async handleCallback(code: string, state: string): Promise<string> {
    const callbackUri = window.location.origin + '/callback'
    await packageAuthService.completeAuthorization(code, state, callbackUri)

    try {
      const profile = await userService.getCurrentUser()
      const userStore = useUserStore()
      userStore.setUser(profile)
    } catch (e) {
      NotifyError(e as Error)
    }

    const internalRedirect =
      sessionStorage.getItem('dx_internal_redirect') || '/'
    sessionStorage.removeItem('dx_internal_redirect')
    return internalRedirect
  }

  /**
   * Refreshes the access token using the package.
   */
  async refreshAccessToken(): Promise<string> {
    return packageAuthService.refreshAccessToken()
  }

  /**
   * Clears local session and redirects to IdP logout / login.
   * @param skipSession When true (default), skips the IdP end-session redirect and
   * navigates directly to /login. Set to false when the user explicitly clicks logout
   * so that the IdP SSO cookie is properly invalidated via the end-session endpoint.
   */
  async logout(skipSession = true): Promise<void> {
    const loginRoute = window.location.origin + '/login'

    try {
      const endSessionUrl = await packageAuthService.logout(
        loginRoute,
        skipSession
      )
      const userStore = useUserStore()
      userStore.setUser(null)

      if (!skipSession && endSessionUrl) {
        window.location.href = endSessionUrl
        return
      }
    } catch (e) {
      NotifyError(e as Error)
    }

    window.location.href = loginRoute
  }

  enableTwoFactorLogin = async () => {
    const response = await this.apiClient.get(
      'authentication/enableTwoFactorLogin'
    )
    return response
  }

  disableTwoFactorLogin = async () => {
    await this.apiClient.post('authentication/disableTwoFactorLogin')
  }

  resendOtp = async () => {
    const response = await this.apiClient.get('authentication/resendOtp', {
      type: 'verification'
    })
    return response
  }

  verfiyOtp = async (otp: string) => {
    const response = await this.apiClient.post(
      'authentication/enableTwoFactorLogin',
      {
        otp: otp
      }
    )
    return response
  }

  resetPassword = async (
    userId: number | undefined,
    currentPassword: string,
    newPassword: string
  ) => {
    await this.apiClient.post(`users/${userId}/resetPassword`, {
      currentPassword,
      newPassword
    })
  }

  clearAuthData = async (cookies: VueCookies) => {
    const appStore = useAppStore()
    const portalDomain =
      import.meta.env.MODE === 'development'
        ? ('localhost' as string)
        : (appStore.getPortalDomain as string)
    localStorage.removeItem('accessToken')
    cookies.remove('user', undefined, portalDomain)
    cookies.remove('accessToken', undefined, portalDomain)
  }

  addAuthData = async (
    cookies: VueCookies,
    user: User | null,
    accessToken: string | null
  ) => {
    const appStore = useAppStore()
    const portalDomain =
      import.meta.env.MODE === 'development'
        ? ('localhost' as string)
        : (appStore.getPortalDomain as string)

    cookies.set(
      'accessToken',
      accessToken ? { accessToken } : null,
      undefined,
      undefined,
      portalDomain
    )

    if (accessToken) {
      localStorage.setItem('accessToken', accessToken)
      packageAuthService.setAccessToken(accessToken)
    } else {
      localStorage.removeItem('accessToken')
    }

    cookies.set('user', user, undefined, undefined, portalDomain)
  }

  sendLoginOtp = async (
    emailAddress: string,
    otpChannels: number[]
  ): Promise<SendLoginOtpResponse> => {
    const payload = {
      portal: useAppStore().getPortalDomain,
      emailAddress,
      otpChannels
    }

    const response = await this.apiClient.post<SendLoginOtpResponse>(
      'authentication/sendLoginOtp',
      payload
    )
    return response
  }

  validateOtpFromSignup = async (
    validateOtpData: ValidateOtpRequest
  ): Promise<{ data: ValidateOtpResponse }> => {
    validateOtpData.clientId =
      (import.meta.env.VITE_APP_DELTAX_CLIENT_APP_ID as string) || ''
    validateOtpData.portal = useAppStore().getPortalDomain

    const response = await this.apiClient.post<{ data: ValidateOtpResponse }>(
      'authentication/ValidateOtpFromSignup',
      validateOtpData,
      {},
      true
    )
    return response
  }
}

export const authService = new AuthService()
export default AuthService
